/**
 * The namespace represents an application interface to manage the navigation cube.
 * 
 * @namespace navigationCube
 * @memberof module:solo.app
 * @since 2.1.1
 * 
 * @prop {number} POSITION_LEFT_TOP=0 Determines the position of the navigation cube on the screen
 * @prop {number} POSITION_LEFT_BOTTOM=1 See above
 * @prop {number} POSITION_RIGHT_TOP=2 See above
 * @prop {number} POSITION_RIGHT_BOTTOM=3 See above
 * 
 * @prop {number} FRONT=1 Defines the front view
 * @prop {number} BACK=2 Defines the back view
 * @prop {number} LEFT=4 Defines the view on the left
 * @prop {number} RIGHT=8 Defines the view on the right
 * @prop {number} TOP=16 Defines the top view
 * @prop {number} BOTTOM=32 Defines the bottom view
 * 
 * @prop {number} ROLL_NONE=0 No rotation of the target view
 * @prop {number} ROLL_CW=1 Rotates the target view clockwise
 * @prop {number} ROLL_CCW=-1 Rotates the target view counterclockwise
*/

var app = require('app').app;

var kDefaultNavigationCubeText = ["FRONT", "BACK", "LEFT", "RIGHT", "TOP", "BOTTOM"];

function getNavigationCubeSideOrientations() {
    var p = Module._tiramisu_getNavigationCubeSideOrientations() >>> 2;
    return [HEAP32[p], HEAP32[p + 1], HEAP32[p + 2], HEAP32[p + 3], HEAP32[p + 4], HEAP32[p + 5]];
}

function NavigationCube(cube) {
    Object.defineProperties(this, {
        /**
         * Gets or sets the visibility flag for the navigation cube.
         * 
         * @static
         * @member {boolean} visible
         * @memberof module:solo.app.navigationCube
         */
        'visible': {
            get: function () { return !!Module._tiramisu_isNavigationCubeVisible(); },
            set: function (visible) { Module._tiramisu_showNavigationCube(visible); },
            enumerable: true
        },
        /**
         * Enables or disables the navigation cube.
         * 
         * @static
         * @member {boolean} enabled
         * @memberof module:solo.app.navigationCube
         */
        'enabled': {
            get: function () { return !!Module._tiramisu_isNavigationCubeEnabled(); },
            set: function (enable) { Module._tiramisu_enableNavigationCube(enable); },
            enumerable: true
        },
        /**
             * Gets or sets the position of the navigation cube in the screen. 
             * See {@link module:solo.app.navigationCube} properties.
             * 
             * @static
             * @member {number} position
             * @memberof module:solo.app.navigationCube
             */
        'position': {
            get: function () { return Module._tiramisu_getNavigationCubePosition(); },
            set: function (position) { Module._tiramisu_setNavigationCubePosition(position); },
            enumerable: true
        },
        /**
         * Gets or sets the size of the navigation cube in logical pixels.
         * 
         * @static
         * @member {number} size
         * @memberof module:solo.app.navigationCube
         */
        'size': {
            get: function () { return Module._tiramisu_getNavigationCubeSize(); },
            set: function (size) { Module._tiramisu_setNavigationCubeSize(size); },
            enumerable: true
        },
        /**
         * Gets or sets the orientation for each side of the navigation cube.
         * Array of 6 integer [front, back, left, right, top, bottom]. 
         * +1 - rotate 90 degrees clockwise, -1 - rotate 90 degrees counterclockwise. For example:
         * - 0 - 0 degree
         * - 1 - 90 degrees clockwise
         * - 2 - 180 degrees clockwise
         * - 3 - 270 degrees clockwise
         * 
         * @static
         * @member {number[]} sideOrientations 
         *  
         * @memberof module:solo.app.navigationCube
         */
        'sideOrientations': {
            get: getNavigationCubeSideOrientations,
            set: function (rotations) {
                var sp = stackSave(),
                    val = stackAlloc(24),
                    p = val >>> 2,
                    i;
                for (i = 0; i < 6; ++i, ++p)
                    HEAP32[p] = rotations[i];
                Module._tiramisu_setNavigationCubeSideOrientations(val);
                stackRestore(sp);
            },
            enumerable: true
        },
        /**
         * Gets or sets the rotation offset for the navigation cube.
         * Array of 4 numbers [x, y, z, angle].
         * 
         * @static
         * @member {number[]} rotationOffset 
         * @memberof module:solo.app.navigationCube
         */
        'rotationOffset': {
            get: function () {
                var p = Module._tiramisu_getNavigationCubeRotationOffset() >>> 2;
                return [HEAPF32[p], HEAPF32[p + 1], HEAPF32[p + 2], HEAPF32[p + 3]];
            },
            set: function (offset) {
                var sp = stackSave(),
                    val = stackAlloc(16),
                    p = val >>> 2;
                HEAPF32[p] = offset[0];
                HEAPF32[p + 1] = offset[1];
                HEAPF32[p + 2] = offset[2];
                HEAPF32[p + 3] = offset[3];
                Module._tiramisu_setNavigationCubeRotationOffset(val);
                stackRestore(sp);
            },
            enumerable: true
        },
        /**
         * Gets or sets an array of labels for all faces of the navigation cube.
         * Array of 6 strings. Default value is
         * ```
         * ["FRONT", "BACK", "LEFT", "RIGHT", "TOP", "BOTTOM"]
         * ```
         * 
         * @static
         * @member {string[]} localizedText
         * @memberof module:solo.app.navigationCube
         */
        'localizedText': {
            get: function () { return cube.localizedText; },
            set: function (localizedText) {
                if (!Array.isArray(localizedText))
                    localizedText = kDefaultNavigationCubeText;
                if (cube.localizedText !== localizedText) {
                    cube.localizedText = localizedText;
                    Module._tiramisu_rebuildNavigationCube();
                }
            },
            enumerable: true
        },
        /**
         * Gets or sets an object with navigation cube style properties.
         * ```
         * {
         *   textColor: '#333',
         *   background: 'rgba(220,220,220,0.75)',
         *   border: 'rgba(64,64,64,0.75)',
         *   hoveredBackground: 'rgba(100,240,255,0.95)',
         *   hoveredBorder: 'rgba(80,180,200,1.0)'
         * }
         * ```
         * 
         * @static
         * @member {object} style 
         * @memberof module:solo.app.navigationCube
         */
        'style': {
            get: function () { return Object.freeze(Object.assign({}, cube.style)); },
            set: function (style) {
                if (style !== null && typeof style === 'object') {
                    if (style.textColor) cube.style.textColor = style.textColor;
                    if (style.background) cube.style.background = style.background;
                    if (style.border) cube.style.border = style.border;
                    if (style.hoveredBackground) cube.style.hoveredBackground = style.hoveredBackground;
                    if (style.hoveredBorder) cube.style.hoveredBorder = style.hoveredBorder;
                    Module._tiramisu_rebuildNavigationCube();
                }
            },
            enumerate: true
        },
        /**
         * Updates the navigation cube texture.
         * 
         * @static
         * @method update 
         * @memberof module:solo.app.navigationCube
         */
        'update': {
            value: function () { Module._tiramisu_rebuildNavigationCube(); },
            enumerate: true
        },
        /**
         * Shows the model at the selected angle.
         * 
         * @static
         * @method rotate 
         * @param {number} view - A combination of bit flags that defines the view. See
         * - app.navigationCube.FRONT = 1
         * - app.navigationCube.BACK = 2
         * - app.navigationCube.LEFT = 4
         * - app.navigationCube.RIGHT = 8
         * - app.navigationCube.TOP = 16
         * - app.navigationCube.BOTTOM = 32
         * @param {number} rollCurrentView - Rotate the target view. See
         * - app.navigationCube.ROLL_NONE = 0
         * - app.navigationCube.ROLL_CW = 1
         * - app.navigationCube.ROLL_CCW = -1
         * @param {boolean} animated
         * @returns {boolean}
         * @memberof module:solo.app.navigationCube
         */
        'rotate': {
            value: function (view, rollCurrentView, animated) {
                return !!Module._tiramisu_setNavigationCubeView(view, rollCurrentView, false, animated, 0);
            },
            enumerate: true
        },
        /**
         * Fits the model at the selected angle.
         * 
         * @static
         * @method fitSceneInView
         * @param {number} view - A combination of bit flags that defines the view. See
         * - app.navigationCube.FRONT = 1
         * - app.navigationCube.BACK = 2
         * - app.navigationCube.LEFT = 4
         * - app.navigationCube.RIGHT = 8
         * - app.navigationCube.TOP = 16
         * - app.navigationCube.BOTTOM = 32
         * @param {number} rollCurrentView - Rotate the target view. See
         * - app.navigationCube.ROLL_NONE = 0
         * - app.navigationCube.ROLL_CW = 1
         * - app.navigationCube.ROLL_CCW = -1
         * @param {boolean} animated
         * @param {number} [fitFactor=0.95]
         * @returns {boolean}
         * @memberof module:solo.app.navigationCube
         * @see {@link module:solo.app.defaultFitFactorScene app.defaultFitFactorScene}
         */
        'fitSceneInView': {
            value: function (view, rollCurrentView, animated, fitFactor) {
                return !!Module._tiramisu_setNavigationCubeView(view, rollCurrentView, true, animated, fitFactor || app.defaultFitFactorScene);
            },
            enumerate: true
        },

        'POSITION_LEFT_TOP': { value: 0, enumerate: true },
        'POSITION_LEFT_BOTTOM': { value: 1, enumerate: true },
        'POSITION_RIGHT_TOP': { value: 2, enumerate: true },
        'POSITION_RIGHT_BOTTOM': { value: 3, enumerate: true },

        'FRONT': { value: 1, enumerate: true },
        'BACK': { value: 2, enumerate: true },
        'LEFT': { value: 4, enumerate: true },
        'RIGHT': { value: 8, enumerate: true },
        'TOP': { value: 16, enumerate: true },
        'BOTTOM': { value: 32, enumerate: true },

        'ROLL_NONE': { value: 0, enumerate: true },
        'ROLL_CW': { value: 1, enumerate: true },
        'ROLL_CCW': { value: -1, enumerate: true },
    });
}


module.exports = {
    NavigationCube: NavigationCube,
    kDefaultNavigationCubeText: kDefaultNavigationCubeText,
    getNavigationCubeSideOrientations: getNavigationCubeSideOrientations
};